import { ref } from "vue";
import { defineStore } from "pinia";
import dayjs from "dayjs";
import { useCoins } from "@/composables/useCoins";
import { appendUnique } from "@/utils/history/appendUnique.js";

export const KYC_STATUS = {
  START: "start",
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error",
};

// Backend kyc_status code -> KYC_STATUS
const KYC_STATUSES_KEYS = {
  0: KYC_STATUS.START,
  1: KYC_STATUS.PENDING,
  2: KYC_STATUS.SUCCESS,
  3: KYC_STATUS.ERROR,
};

export const useUserStore = defineStore("user", () => {
  const isAuth = ref(false);
  const userData = ref(null);

  const captchaToken = ref(null);
  const referralIncomesStatistic = ref(null);

  const dashboardOnboardingData = ref(null);

  const setDashboardOnboardingData = (data) => {
    if (!data) {
      dashboardOnboardingData.value = data;
      return;
    }

    dashboardOnboardingData.value = {
      ...data,
      steps: data.steps
        ? [...data.steps].sort(
            (a, b) => Number(b.completed) - Number(a.completed),
          )
        : data.steps,
    };
  };

  const getDashboardOnboardingData = () => {
    return dashboardOnboardingData.value;
  };

  const setReferralIncomesStatistic = (data) => {
    referralIncomesStatistic.value = data;
  };

  const getReferralIncomesStatistic = () => {
    return referralIncomesStatistic.value;
  };

  const setCaptchaToken = (data) => {
    captchaToken.value = data;
  };

  const getCaptchaToken = () => {
    return captchaToken.value;
  };

  const setUserData = (data) => {
    userData.value = data;
  };

  const authForm = ref({
    email: "",
    password: "",
  });

  const registerForm = ref({
    email: "",
    password: "",
  });

  const verifyPhone = ref(null);
  const setVerifyPhone = (data) => {
    verifyPhone.value = data;
  };

  const verifyEmail = ref(null);
  const setVerifyEmail = (data) => {
    verifyEmail.value = data;
  };

  const kycStatus = ref(0);
  const setKycStatus = (data) => {
    kycStatus.value = data;
  };

  const getKycStatus = () => {
    return KYC_STATUSES_KEYS[kycStatus.value];
  };

  const userIdForAuthEmail = ref(null);
  const setUserIdForAuthEmail = (data) => {
    userIdForAuthEmail.value = data;
  };

  const authNeedEmail = ref(false);
  const setAuthNeedEmail = (data) => {
    authNeedEmail.value = data;
  };

  const setAuth = () => {
    isAuth.value = true;
  };

  const removeAuth = () => {
    isAuth.value = false;
    authForm.value.email = "";
    authForm.value.password = "";
  };

  const transactions = ref({
    total: 0,
    items: [],
  });
  // append — следующая порция истории (offset > 0). created_at (unix, сек) — для группировки
  // по дням в components/main/history/history.vue
  const setTransactions = (data, { append = false } = {}) => {
    const coinsList = useCoins().coins.value;
    const items = (data.wallets ?? []).map((item) => ({
      ...item,
      created_at: dayjs(item.date).unix(),
      coin: coinsList.find(
        (coin) => coin.config.legacy_coin_id === item.coin_id,
      ),
      exchange_to_coin: item.exchange_to_coin_id
        ? coinsList.find(
            (coin) => coin.config.legacy_coin_id === item.exchange_to_coin_id,
          )
        : undefined,
    }));
    transactions.value = {
      total: data.total_count,
      items: append ? appendUnique(transactions.value.items, items) : items,
    };
  };

  // 2FA/secure переехали в auth-service (state.auth.secure, читается
  // useAuth()); здесь остаются только сессии/уведомления/kyc.
  const settings = ref({
    kyc: null,
    sessions: [],
    sessionsCount: 0,
    notifications: [],
  });

  const setSessions = (data) => {
    settings.value.sessionsCount = data.count;
    settings.value.sessions = data.sessions;
  };

  const setNotifications = (data) => {
    settings.value.notifications = data.notification_groups;
  };

  return {
    isAuth,
    setAuth,
    authForm,
    registerForm,
    setUserData,
    userData,
    settings,
    setSessions,
    setNotifications,
    verifyPhone,
    setVerifyPhone,
    verifyEmail,
    setVerifyEmail,
    transactions,
    setTransactions,
    removeAuth,
    userIdForAuthEmail,
    setUserIdForAuthEmail,
    authNeedEmail,
    setAuthNeedEmail,
    getKycStatus,
    setKycStatus,
    setCaptchaToken,
    getCaptchaToken,
    setReferralIncomesStatistic,
    getReferralIncomesStatistic,
    setDashboardOnboardingData,
    getDashboardOnboardingData,
  };
});
